import { useMemo, useState, type ReactNode } from 'react'
import { ArrowDown, ArrowUp } from 'lucide-react'

import { cn } from '../lib/cn'
import { t } from '../lib/i18n'

export interface Column<Row> {
  id: string
  header: ReactNode
  /** Right-align the cell. Numbers and money always. */
  numeric?: boolean
  /** Omit to make the column unsortable — which is the correct default. */
  sortBy?: (row: Row) => string | number
  cell: (row: Row) => ReactNode
  width?: string
  /** Hidden on narrow viewports rather than crushed. */
  secondary?: boolean
}

export interface DataTableProps<Row> {
  columns: Column<Row>[]
  rows: Row[]
  rowKey: (row: Row) => string
  /** Rendered at the end of the row, revealed on hover. */
  rowActions?: (row: Row) => ReactNode
  onRowClick?: (row: Row) => void
  caption?: string
  className?: string
  /** Initial sort. Sorting is client-side because these are fixtures. */
  defaultSort?: { columnId: string; direction: 'asc' | 'desc' }
  empty?: ReactNode
}

/**
 * The table both consoles use. Column definitions rather than hand-written
 * `<tr>`s, so "sees money" behaves the same on every screen and a column can
 * simply be absent for a role instead of being blanked out.
 *
 * It owns its own horizontal scroll container: a table that widens the page
 * makes every other panel scroll sideways with it.
 */
export function DataTable<Row>({
  columns,
  rows,
  rowKey,
  rowActions,
  onRowClick,
  caption,
  className,
  defaultSort,
  empty,
}: DataTableProps<Row>) {
  const [sort, setSort] = useState(defaultSort ?? null)

  const sorted = useMemo(() => {
    if (!sort) return rows
    const column = columns.find((c) => c.id === sort.columnId)
    if (!column?.sortBy) return rows
    const read = column.sortBy
    return [...rows].sort((a, b) => {
      const av = read(a)
      const bv = read(b)
      const order = typeof av === 'number' && typeof bv === 'number'
        ? av - bv
        : String(av).localeCompare(String(bv))
      return sort.direction === 'asc' ? order : -order
    })
  }, [rows, sort, columns])

  const toggle = (column: Column<Row>) => {
    if (!column.sortBy) return
    setSort((current) =>
      current?.columnId === column.id
        ? { columnId: column.id, direction: current.direction === 'asc' ? 'desc' : 'asc' }
        : { columnId: column.id, direction: 'asc' },
    )
  }

  if (rows.length === 0 && empty) return <>{empty}</>

  return (
    <div className={cn('-mx-3 overflow-x-auto px-3', className)}>
      <table className="w-full border-collapse">
        {caption ? <caption className="sr-only">{caption}</caption> : null}
        <thead>
          <tr>
            {columns.map((column) => {
              const active = sort?.columnId === column.id
              return (
                <th
                  key={column.id}
                  scope="col"
                  style={column.width ? { width: column.width } : undefined}
                  className={cn(
                    'sticky top-0 z-10 border-b border-muted/25 bg-glass-heavy px-3 pb-[11px]',
                    'font-display text-[10.5px] font-bold tracking-[0.08em] whitespace-nowrap text-muted uppercase',
                    column.numeric ? 'text-end' : 'text-start',
                    column.secondary && 'hidden lg:table-cell',
                  )}
                >
                  {column.sortBy ? (
                    <button
                      type="button"
                      onClick={() => toggle(column)}
                      className={cn(
                        'inline-flex items-center gap-1 uppercase',
                        active && 'text-accent-dark',
                      )}
                      aria-label={t('table.sortedBy', { column: column.id })}
                    >
                      {column.header}
                      {active ? (
                        sort?.direction === 'asc' ? (
                          <ArrowUp className="size-3" />
                        ) : (
                          <ArrowDown className="size-3" />
                        )
                      ) : null}
                    </button>
                  ) : (
                    column.header
                  )}
                </th>
              )
            })}
            {rowActions ? (
              <th scope="col" className="sticky top-0 z-10 border-b border-muted/25 bg-glass-heavy px-3 pb-[11px]">
                <span className="sr-only">{t('table.rowActions')}</span>
              </th>
            ) : null}
          </tr>
        </thead>
        <tbody>
          {sorted.map((row) => (
            <tr
              key={rowKey(row)}
              onClick={onRowClick ? () => onRowClick(row) : undefined}
              className={cn(
                'group border-b border-muted/12 last:border-b-0',
                'hover:bg-glass',
                onRowClick && 'cursor-pointer',
              )}
            >
              {columns.map((column) => (
                <td
                  key={column.id}
                  className={cn(
                    'px-3 py-[13px] align-middle text-[13.5px] text-dark',
                    column.numeric ? 'text-end' : 'text-start',
                    column.secondary && 'hidden lg:table-cell',
                  )}
                >
                  {column.cell(row)}
                </td>
              ))}
              {rowActions ? (
                <td className="px-3 py-[13px] text-end">
                  <div className="flex justify-end gap-1.5 opacity-55 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100">
                    {rowActions(row)}
                  </div>
                </td>
              ) : null}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

/** The small square control used inside `rowActions`. */
export function RowAction({
  label,
  icon,
  tone = 'default',
  onClick,
}: {
  label: string
  icon: ReactNode
  tone?: 'default' | 'danger'
  onClick?: () => void
}) {
  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      onClick={(event) => {
        event.stopPropagation()
        onClick?.()
      }}
      className={cn(
        'flex size-[30px] items-center justify-center rounded-lg border border-glass-border bg-glass',
        tone === 'danger' ? 'text-danger' : 'text-sub',
      )}
    >
      <span className="flex size-3.5 items-center justify-center">{icon}</span>
    </button>
  )
}
